import { useEffect, useId, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Switch,
  Typography,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

import SourceConnectorList from '@/components/chat/SourceConnectorList';
import { EXTERNAL_URLS } from '@/constants/urls';
import useAnalytics from '@/hooks/useAnalytics';
import { semanticColors } from '@/theme/tokens';
import { TOGGLE_SX } from '@/ui-library/switch.styles';
import { isGpcEnabled, useAdConsent } from '@/utils/adConsentStore';
import { getInputModality, installInputModalityTracker } from '@/utils/inputModality';

import SaveStatus from './SaveStatus';
import { SettingsRow, SettingsSection, useSettingsSurface } from './SettingsSection';
import type { SettingsPanelProps } from './settingsTypes';

export function SourcesSettingsSection() {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const surface = useSettingsSurface();
  const analytics = useAnalytics();
  const [expanded, setExpanded] = useState(false);
  const sourcesId = useId();

  if (surface === 'dialog') {
    return (
      <SettingsSection sx={{ borderTop: `1px solid ${semanticColors.borderLight}`, pt: 1 }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expanded}
          onChange={(_, nextExpanded) => {
            setExpanded(nextExpanded);
            analytics.track('Settings Sources Toggled', { expanded: nextExpanded, surface });
          }}
          slotProps={{ transition: { unmountOnExit: true } }}
          sx={{ backgroundColor: 'transparent', '&::before': { display: 'none' } }}
        >
          <AccordionSummary
            id={`${sourcesId}-summary`}
            aria-controls={`${sourcesId}-details`}
            expandIcon={
              <ExpandMoreIcon sx={{ fontSize: 20, color: semanticColors.textTertiary }} />
            }
            sx={{ px: 0, minHeight: 44, '& .MuiAccordionSummary-content': { my: 1 } }}
          >
            <Typography
              component="span"
              sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1.4 }}
            >
              {tCommon('sourceConnectors.title')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
            <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary, mb: 1 }}>
              {tCommon('sourceConnectors.alwaysOnNote')}
            </Typography>
            <SourceConnectorList surface="settings" statusPresentation="text" />
          </AccordionDetails>
        </Accordion>
        <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary, mt: 0.5 }}>
          {t('sourcesLocalNote')}
        </Typography>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection>
      <Typography component="h3" sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1.4 }}>
        {tCommon('sourceConnectors.title')}
      </Typography>
      <Typography sx={{ fontSize: '13px', color: semanticColors.textTertiary, mt: 0.5 }}>
        {tCommon('sourceConnectors.alwaysOnNote')}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: semanticColors.textTertiary, mt: 1, mb: 1 }}>
        {t('sourcesLocalNote')}
      </Typography>
      <SourceConnectorList surface="settings" />
    </SettingsSection>
  );
}

export function AiDataSettingsSection() {
  const { t } = useTranslation('settings');
  const surface = useSettingsSurface();
  return (
    <SettingsSection>
      <Typography component="h3" sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1.4 }}>
        {t('aiDataTitle')}
      </Typography>
      <Typography
        sx={{
          fontSize: surface === 'dialog' ? '14px' : '13px',
          color: semanticColors.textTertiary,
          mt: 0.75,
          lineHeight: 1.6,
          ...(surface === 'dialog' ? { maxWidth: '64ch' } : {}),
        }}
      >
        {t('aiDataDescription')}
      </Typography>
    </SettingsSection>
  );
}

export function LegalPrivacySettingsSection({
  settings,
  focusPrivacy = false,
  privacyArrival = 0,
  onPrivacyArrival,
}: SettingsPanelProps & {
  focusPrivacy?: boolean;
  privacyArrival?: number;
  onPrivacyArrival?: () => void;
}) {
  const { t } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const surface = useSettingsSurface();
  const adLabel =
    surface === 'dialog' ? t('advertisingSharingLabel') : tCommon('privacyChoices.adPartners');
  const privacySection = useRef<HTMLElement>(null);
  const privacyTitle = useRef<HTMLHeadingElement>(null);
  const gpc = isGpcEnabled();
  const adConsent = useAdConsent();
  const adGranted = !gpc && adConsent?.granted !== false;
  const adStatus = gpc
    ? tCommon('privacyChoices.gpcOn')
    : tCommon(adGranted ? 'privacyChoices.adPartnersOn' : 'privacyChoices.adPartnersOff');

  useEffect(() => installInputModalityTracker(), []);
  useEffect(() => {
    if (!focusPrivacy || !privacySection.current) return;
    privacySection.current.scrollIntoView({ block: 'start' });
    if (getInputModality() === 'keyboard') privacyTitle.current?.focus({ preventScroll: true });
    onPrivacyArrival?.();
  }, [focusPrivacy, privacyArrival, onPrivacyArrival]);

  const policyLinks = [
    { label: t('termsAndConditions'), href: EXTERNAL_URLS.TERMS_OF_USE },
    { label: t('privacyPolicy'), href: EXTERNAL_URLS.PRIVACY_POLICY },
    { label: t('responsibleAI'), href: EXTERNAL_URLS.RESPONSIBLE_AI },
  ];

  return (
    <>
      <SettingsSection>
        <Box
          ref={privacySection}
          component="section"
          id="privacy-choices"
          aria-labelledby="settings-privacy-title"
          sx={{ scrollMarginTop: '16px' }}
        >
          <Typography
            ref={privacyTitle}
            component="h3"
            id="settings-privacy-title"
            tabIndex={-1}
            sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1.4, outline: 'none' }}
          >
            {tCommon('privacyChoices.title')}
          </Typography>
          <Typography
            sx={{
              fontSize: surface === 'dialog' ? '14px' : '13px',
              color: semanticColors.textTertiary,
              mt: 0.75,
              lineHeight: 1.6,
              ...(surface === 'dialog' ? { maxWidth: '64ch', mb: 0.5 } : {}),
            }}
          >
            {tCommon('privacyChoices.description')}
          </Typography>
          <SettingsRow
            label={adLabel}
            description={adStatus}
            descriptionId="settings-ad-partners-desc"
            controlAlignment={surface === 'dialog' ? 'start' : 'center'}
            control={
              <Switch
                checked={adGranted}
                onChange={(event) => settings.setAdConsentChoice(event.target.checked)}
                disabled={gpc || settings.groups.adConsent.state === 'saving'}
                sx={TOGGLE_SX}
                slotProps={{
                  input: {
                    'aria-label': adLabel,
                    'aria-describedby': 'settings-ad-partners-desc',
                  },
                }}
              />
            }
            status={
              <SaveStatus
                state={settings.groups.adConsent.state}
                error={settings.groups.adConsent.error}
                savingLabel={t('saving')}
                savedLabel={t('saved')}
              />
            }
          />
        </Box>
      </SettingsSection>
      <SettingsSection>
        <Typography component="h3" sx={{ fontWeight: 600, fontSize: '15px', lineHeight: 1.4 }}>
          {t('legalPrivacy')}
        </Typography>
        <Typography
          sx={{
            fontSize: surface === 'dialog' ? '14px' : '13px',
            color: semanticColors.textTertiary,
            mt: 0.75,
            mb: 1,
            lineHeight: 1.6,
            ...(surface === 'dialog' ? { maxWidth: '64ch' } : {}),
          }}
        >
          {t('policyLinksNote')}
        </Typography>
        {policyLinks.map((item, index) => (
          <Box key={item.href}>
            <Box
              component="a"
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 1.5,
                color: semanticColors.textPrimary,
                textDecoration: 'none',
                '&:hover': { color: semanticColors.brandAccent },
                '&:focus-visible': {
                  outline: `2px solid ${semanticColors.brandAccent}`,
                  outlineOffset: 2,
                },
              }}
            >
              <Typography sx={{ fontSize: '14px' }}>{item.label}</Typography>
              <ArrowForwardIosIcon sx={{ fontSize: 13, color: semanticColors.textTertiary }} />
            </Box>
            {index < policyLinks.length - 1 && <Divider />}
          </Box>
        ))}
      </SettingsSection>
    </>
  );
}
